import React, { useEffect, useState } from "react";
import { Box, Button, Divider, IconButton, Modal, TextField, Typography } from "@mui/material";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import theme from "../../../../../Layouts/theme";

const ExternalCandidateModal = ({ open, onClose, onSuccess }) => {
  const intl = useIntl();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();
  const isDesktop = useMediaQuery((muiTheme) => muiTheme.breakpoints.up("md"));

  const [submitting, setSubmitting] = useState(false);
  const [jobOffersLoading, setJobOffersLoading] = useState(false);
  const [jobOffers, setJobOffers] = useState([]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    job_offer_id: null,
    resume: null,
  });

  const resetForm = () => {
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      job_offer_id: null,
      resume: null,
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
    resetForm();
  };

  const isSubmitDisabled = submitting || !form.email.trim() || !form.first_name.trim() || !form.last_name.trim() || !form.job_offer_id;

  useEffect(() => {
    if (!open) return;

    setJobOffersLoading(true);
    sh.get("job_offer", {
      authenticity_token: authenticityToken,
      params: { offer_status: "online" },
    })
      .then((res) => {
        setJobOffers(res?.data?.data?.job_offers || []);
      })
      .catch((e) => {
        console.error(e);
        enqueueSnackbar(
          intl.formatMessage({ id: "recruiter.cv_theque.error_loading_job_offers" }),
          { variant: "error" }
        );
      })
      .finally(() => setJobOffersLoading(false));
  }, [open, authenticityToken, enqueueSnackbar, intl]);

  const submitExternalCandidate = async () => {
    if (isSubmitDisabled) return;

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("authenticity_token", authenticityToken);
      payload.append("job_offer_id", String(form.job_offer_id));
      payload.append("external_candidate[first_name]", form.first_name.trim());
      payload.append("external_candidate[last_name]", form.last_name.trim());
      payload.append("external_candidate[phone]", form.phone.trim());
      payload.append("external_candidate[email]", form.email.trim());
      if (form.resume) payload.append("external_candidate[resume]", form.resume);

      await sh.post("/add_candidate_to_kanban", payload);
      enqueueSnackbar(
        intl.formatMessage({ id: "recruiter.kanban.external_candidate_added" }),
        { variant: "success" }
      );

      if (typeof onSuccess === "function") onSuccess();
      handleClose();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(
        e?.response?.data?.error ||
          intl.formatMessage({ id: "recruiter.cv_theque.error_adding_to_kanban" }),
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "#F9F9F9",
      border: `1px solid ${theme.palette.secondary.grey_strong}`,
      "& fieldset": { border: "none" },
    },
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
    >
      <Box
        sx={{
          width: "min(620px, calc(100vw - 24px))",
          backgroundColor: "#FFF",
          borderRadius: "20px",
          border: `1px solid ${theme.palette.background.neutral}`,
          boxShadow: "0px 20px 44px rgba(2, 51, 49, 0.14)",
          outline: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            p: "20px 20px 14px",
            background: "linear-gradient(180deg, #F9FCFF 0%, #FFFFFF 100%)",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "20px", color: theme.palette.text.main }}>
              <FormattedMessage id="recruiter.kanban.add_external_candidate" />
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "13px", color: theme.palette.text.secondary }}>
              <FormattedMessage id="recruiter.kanban.add_external_candidate_subtitle" />
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: theme.palette.text.secondary, mt: "-2px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: "18px 20px 20px", position: "relative" }}>
          {jobOffersLoading ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.75)",
                borderRadius: "8px",
              }}
            >
              <SquarehubLoader size={72} />
            </Box>
          ) : null}
          <Typography sx={{ mb: 1.5, fontFamily: "Segoe UI Semibold, sans-serif", color: theme.palette.text.main, fontSize: "14px" }}>
            <FormattedMessage id="recruiter.kanban.candidate_information" />
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr", gap: "12px" }}>
            <TextField
              required
              label={intl.formatMessage({ id: "recruiter.kanban.first_name" })}
              value={form.first_name}
              onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
              size="small"
              sx={fieldSx}
            />
            <TextField
              required
              label={intl.formatMessage({ id: "recruiter.kanban.last_name" })}
              value={form.last_name}
              onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
              size="small"
              sx={fieldSx}
            />
            <TextField
              label={intl.formatMessage({ id: "recruiter.kanban.phone" })}
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              size="small"
              sx={fieldSx}
            />
            <TextField
              required
              type="email"
              label={intl.formatMessage({ id: "recruiter.kanban.email" })}
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              size="small"
              sx={fieldSx}
            />
          </Box>

          <TextField
            required
            fullWidth
            select
            SelectProps={{ native: true }}
            value={form.job_offer_id ?? ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                job_offer_id: e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            size="small"
            disabled={jobOffersLoading}
            sx={{ ...fieldSx, mt: 2 }}
          >
            <option value="">
              {jobOffersLoading
                ? intl.formatMessage({ id: "loading" })
                : intl.formatMessage({ id: "recruiter.cv_theque.select_job_offer" })}
            </option>
            {(jobOffers || []).map((offer) => (
              <option key={offer.id} value={offer.id}>
                {offer.job_title}
              </option>
            ))}
          </TextField>

          <Box
            sx={{
              mt: 1.5,
              border: `1px dashed ${theme.palette.secondary.grey_strong}`,
              borderRadius: "12px",
              p: "10px 12px",
              backgroundColor: "#FFF",
            }}
          >
            <Typography sx={{ fontSize: "12px", color: theme.palette.text.secondary, mb: 1 }}>
              <FormattedMessage id="recruiter.kanban.resume_optional" />
            </Typography>
            <Button
              component="label"
              sx={{
                padding: "6px 24px",
                borderRadius: "29px",
                textTransform: "none",
                border: `1px solid ${theme.palette.secondary.grey_strong}`,
                color: theme.palette.text.main,
                fontWeight: "800",
                minWidth: "unset",
                "&:hover": {
                  backgroundColor: theme.palette.secondary.hover,
                },
              }}
            >
              <Typography sx={{ fontSize: "14px", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                <FormattedMessage id="recruiter.kanban.upload_resume" />
              </Typography>
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    resume: e.target.files?.[0] || null,
                  }))
                }
              />
            </Button>
            {form.resume ? (
              <Typography sx={{ mt: 1, fontSize: "12px", color: theme.palette.text.main, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                {form.resume.name}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: "14px 20px", display: "flex", justifyContent: "flex-end", gap: "10px", backgroundColor: "#FBFBFB" }}>
          <Button onClick={handleClose} disabled={submitting}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="contained"
            onClick={submitExternalCandidate}
            disabled={isSubmitDisabled}
            sx={{
              borderRadius: "999px",
              backgroundColor: theme.palette.text.main,
              color: "#F1F1F1",
              textTransform: "none",
              fontWeight: 800,
              px: 2,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme.palette.secondary.hover,
                color: theme.palette.text.main,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.secondary.grey_strong,
                color: theme.palette.text.light_grey,
              },
            }}
          >
            {submitting ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 22 }}>
                <SquarehubLoader size={22} />
              </Box>
            ) : (
              <FormattedMessage id="add" />
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExternalCandidateModal;
